const MIRROR_TTL_MS = 7 * 24 * 60 * 60 * 1000

export const FRAGMENT_CLAIMS = {
  link: {
    name: 'link',
    key: 't',
    routes: ['/pay', '/bank-account'],
    shape: /^[0-9a-f]{32,}$/i,
    mirrorPrefix: 'lnk:',
  },
  passwordSetup: {
    name: 'passwordSetup',
    key: 't',
    routes: ['/create-password'],
    shape: /^[0-9a-f]{64}$/i,
    mirrorPrefix: 'pst:',
  },
  docAccess: {
    name: 'docAccess',
    key: 'd',
    routes: ['/esign-doc'],
    shape: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    mirrorPrefix: 'doc:',
  },
}

export const ownsRoute = (routes, pathname) => {
  const path = pathname || ''
  return (routes || []).some((route) => path === route || path.startsWith(`${route}/`))
}

const unanchored = (shape) => shape.source.replace(/^\^/, '').replace(/\$$/, '')

const fragmentPattern = (claim) => new RegExp(`[#&]${claim.key}=(${unanchored(claim.shape)})\\b`, 'i')

const mirrorKey = (claim, pathname) => `${claim.mirrorPrefix}${pathname}`

const readMirror = (claim, pathname) => {
  try {
    const raw = window.sessionStorage.getItem(mirrorKey(claim, pathname))
    if (!raw) return null

    const { token, at } = JSON.parse(raw)
    if (!token || typeof at !== 'number' || Date.now() - at > MIRROR_TTL_MS) {
      window.sessionStorage.removeItem(mirrorKey(claim, pathname))
      return null
    }

    return token
  } catch (error) {
    return null
  }
}

export const writeMirror = (claim, pathname, token) => {
  try {
    window.sessionStorage.setItem(mirrorKey(claim, pathname), JSON.stringify({ token, at: Date.now() }))
  } catch (error) {
    // Private-mode Safari and embedded webviews refuse sessionStorage outright; the value is
    // already in memory, so only reload recovery is lost.
  }
}

export const replaceUrl = (pathname, search, hash) => {
  try {
    window.history.replaceState(null, '', `${pathname}${search}${hash ? `#${hash}` : ''}`)
  } catch (error) {
    // Some embedded webviews refuse replaceState; the value is already in memory.
  }
}

// Every caller must invoke this at MODULE SCOPE. src/index.js calls Sentry.init after importing
// ./App, and Replay snapshots the initial URL inside init — a useEffect loses that race and the
// credential is recorded.
export const captureFragmentCredential = (claim) => {
  if (typeof window === 'undefined') return null

  const { pathname, search, hash } = window.location
  if (!ownsRoute(claim.routes, pathname)) return null

  const match = (hash || '').match(fragmentPattern(claim))
  if (!match) return readMirror(claim, pathname)

  writeMirror(claim, pathname, match[1])
  replaceUrl(pathname, search, (hash || '').replace(match[0], '').replace(/^[#&]+/, ''))

  return match[1]
}

export const clearFragmentCredential = (claim) => {
  try {
    window.sessionStorage.removeItem(mirrorKey(claim, window.location.pathname))
  } catch (error) {
    // Storage refused; the caller has already dropped its in-memory copy.
  }
}

export const findClaimConflicts = (claims) => {
  const entries = Object.values(claims || {})
  const conflicts = []

  for (let i = 0; i < entries.length; i += 1) {
    for (let j = i + 1; j < entries.length; j += 1) {
      const a = entries[i]
      const b = entries[j]

      if (a.key !== b.key) continue

      const overlaps = a.routes.some((route) => ownsRoute(b.routes, route))
        || b.routes.some((route) => ownsRoute(a.routes, route))

      if (overlaps) conflicts.push({ key: a.key, a: a.name, b: b.name })
    }
  }

  return conflicts
}
